import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layout, Card, Typography, Button, Table, Space, InputNumber, Input, message, Spin,
  DatePicker, Select, Tabs, Tag, Row, Col, Statistic, Tooltip, Alert,
} from 'antd';
import {
  ArrowLeftOutlined, DeleteOutlined, ClockCircleOutlined,
  UserOutlined, CalendarOutlined, FieldTimeOutlined, TeamOutlined,
  DollarOutlined, PlusOutlined, MinusOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { useDoctorContext } from './DoctorContext';
import { useClinicContext } from './ClinicContext';
import { useLanguage } from './LanguageContext';
import { useOwnerContext } from './OwnerContext';
import API from '../config/api';
import { getAssistantsForDoctorClinic } from '../services/notificationService';
import { getClinicDoctorOptions } from '../utils/clinicDoctors';
import ClinicAttendanceZoneSettings from './ClinicAttendanceZoneSettings';
import MissingCheckoutCorrectionModal from './MissingCheckoutCorrectionModal';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/* ─── Types ─────────────────────────────────────────────────────────────────── */

interface AttendanceLog {
  _id: string;
  assistant_id: string;
  assistant_name: string;
  assistant_email?: string;
  doctor_id: string;
  clinic_id: string;
  clinic_name: string;
  action: 'sign_in' | 'sign_out';
  latitude: number;
  longitude: number;
  distance_from_center_m?: number;
  createdAt: string;
  needs_review?: boolean;
  review_status?: 'pending' | 'resolved';
  review_reason?: 'missing_sign_out' | 'missing_sign_in';
}

interface Session {
  key: string;
  date: string;
  assistantId: string;
  assistantName: string;
  clinicName: string;
  signInAt: string | null;
  signOutAt: string | null;
  durationMinutes: number | null;
  signInLogId: string;
  clinicId: string;
  needsReview: boolean;
  reviewReason: 'missing_sign_out' | 'missing_sign_in' | null;
}

interface DaySummary {
  key: string;
  date: string;
  sessions: Session[];
  totalMinutes: number;
}

interface AssistantSummary {
  key: string;
  assistantId: string;
  assistantName: string;
  totalSessions: number;
  daysWorked: number;
  totalMinutes: number;
  avgMinutesPerDay: number;
  days: DaySummary[];
}

interface AssistantPayrollLine {
  clientKey: string;
  amount: number;
  reason: string;
  created_at?: string;
  lineKind?: 'bonus' | 'discount';
}

interface AssistantPayrollState {
  hourlyRate: number;
  /** Fixed monthly pay (e.g. EGP); hourly = monthly ÷ (hoursPerDay × workingDays) when all set */
  monthlySalary: number;
  hoursPerDay: number;
  workingDays: number;
  lines: AssistantPayrollLine[];
}

/* ─── Helpers ────────────────────────────────────────────────────────────────── */

function formatDuration(minutes: number | null): string {
  if (minutes === null || minutes < 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function formatHoursDecimal(minutes: number): string {
  return (minutes / 60).toFixed(1) + 'h';
}

function minutesInYearMonth(assistant: AssistantSummary, yearMonth: string | null): number {
  if (!yearMonth) return 0;
  return assistant.days
    .filter((d) => d.date.slice(0, 7) === yearMonth)
    .reduce((s, d) => s + d.totalMinutes, 0);
}

function effectiveLineKind(line: AssistantPayrollLine): 'bonus' | 'discount' {
  if (line.lineKind === 'bonus' || line.lineKind === 'discount') return line.lineKind;
  return (Number(line.amount) || 0) < 0 ? 'discount' : 'bonus';
}

/** Mon–Fri count in a YYYY-MM calendar month */
function countWeekdaysMonFri(yearMonth: string): number {
  const start = dayjs(`${yearMonth}-01`);
  const n = start.daysInMonth();
  let c = 0;
  for (let d = 1; d <= n; d++) {
    const w = start.date(d).day();
    if (w >= 1 && w <= 5) c += 1;
  }
  return c;
}

function defaultAssistantPayroll(yearMonth: string | null): AssistantPayrollState {
  return {
    hourlyRate: 0,
    monthlySalary: 0,
    hoursPerDay: 8,
    workingDays: yearMonth ? countWeekdaysMonFri(yearMonth) : 22,
    lines: [],
  };
}

function expectedContractHours(state: AssistantPayrollState): number {
  const h = Number(state.hoursPerDay) || 0;
  const d = Number(state.workingDays) || 0;
  return h > 0 && d > 0 ? h * d : 0;
}

function effectiveHourlyRate(state: AssistantPayrollState): number {
  const exp = expectedContractHours(state);
  if (state.monthlySalary > 0 && exp > 0) {
    return Math.round((state.monthlySalary / exp) * 10000) / 10000;
  }
  return Number(state.hourlyRate) || 0;
}

function roundedWorkedMinutes(start: string, endMs: number): number {
  const elapsedMs = endMs - new Date(start).getTime();
  if (elapsedMs <= 0) return 0;
  // A real started session must not disappear as "0 min" merely because it
  // lasted less than 30 seconds (Math.round previously did exactly that).
  return Math.max(1, Math.ceil(elapsedMs / 60000));
}

function pairLogsToSessions(logs: AttendanceLog[], nowMs = Date.now()): Session[] {
  const sorted = [...logs].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
  const sessions: Session[] = [];
  let pending: AttendanceLog | null = null;

  for (const log of sorted) {
    if (log.action === 'sign_in') {
      if (pending) {
        // Unclosed sign_in — record without sign_out
        sessions.push({
          key: pending._id,
          date: dayjs(pending.createdAt).format('YYYY-MM-DD'),
          assistantId: pending.assistant_id,
          assistantName: pending.assistant_name,
          clinicName: pending.clinic_name || pending.clinic_id,
          signInAt: pending.createdAt,
          signOutAt: null,
          durationMinutes: null,
          signInLogId: pending._id,
          clinicId: pending.clinic_id,
          needsReview: pending.needs_review === true
            || dayjs(pending.createdAt).format('YYYY-MM-DD') < dayjs(nowMs).format('YYYY-MM-DD'),
          reviewReason: 'missing_sign_out',
        });
      }
      pending = log;
    } else if (log.action === 'sign_out') {
      if (pending) {
        const sameDay = dayjs(pending.createdAt).format('YYYY-MM-DD')
          === dayjs(log.createdAt).format('YYYY-MM-DD');
        sessions.push({
          key: pending._id,
          date: dayjs(pending.createdAt).format('YYYY-MM-DD'),
          assistantId: pending.assistant_id,
          assistantName: pending.assistant_name,
          clinicName: pending.clinic_name || pending.clinic_id,
          signInAt: pending.createdAt,
          signOutAt: sameDay ? log.createdAt : null,
          durationMinutes: sameDay
            ? roundedWorkedMinutes(pending.createdAt, new Date(log.createdAt).getTime())
            : null,
          signInLogId: pending._id,
          clinicId: pending.clinic_id,
          needsReview: !sameDay || pending.needs_review === true,
          reviewReason: sameDay ? null : 'missing_sign_out',
        });
        if (!sameDay) {
          sessions.push({
            key: log._id,
            date: dayjs(log.createdAt).format('YYYY-MM-DD'),
            assistantId: log.assistant_id,
            assistantName: log.assistant_name,
            clinicName: log.clinic_name || log.clinic_id,
            signInAt: null,
            signOutAt: log.createdAt,
            durationMinutes: null,
            signInLogId: '',
            clinicId: log.clinic_id,
            needsReview: true,
            reviewReason: 'missing_sign_in',
          });
        }
        pending = null;
      } else {
        sessions.push({
          key: log._id,
          date: dayjs(log.createdAt).format('YYYY-MM-DD'),
          assistantId: log.assistant_id,
          assistantName: log.assistant_name,
          clinicName: log.clinic_name || log.clinic_id,
          signInAt: null,
          signOutAt: log.createdAt,
          durationMinutes: null,
          signInLogId: '',
          clinicId: log.clinic_id,
          needsReview: true,
          reviewReason: 'missing_sign_in',
        });
      }
    }
  }

  if (pending) {
    const pendingStartedToday = dayjs(pending.createdAt).isSame(dayjs(nowMs), 'day');
    sessions.push({
      key: pending._id,
      date: dayjs(pending.createdAt).format('YYYY-MM-DD'),
      assistantId: pending.assistant_id,
      assistantName: pending.assistant_name,
      clinicName: pending.clinic_name || pending.clinic_id,
      signInAt: pending.createdAt,
      signOutAt: null,
      // Show elapsed time while today's session is still open. Old unclosed
      // days remain incomplete instead of being inflated into multi-day shifts.
      durationMinutes: pendingStartedToday
        ? roundedWorkedMinutes(pending.createdAt, nowMs)
        : null,
      signInLogId: pending._id,
      clinicId: pending.clinic_id,
      needsReview: pending.needs_review === true || !pendingStartedToday,
      reviewReason: pendingStartedToday ? null : 'missing_sign_out',
    });
  }

  return sessions;
}

function buildAssistantSummaries(logs: AttendanceLog[], nowMs = Date.now()): AssistantSummary[] {
  // Group logs by assistant_id
  const byAssistant: Record<string, AttendanceLog[]> = {};
  for (const log of logs) {
    if (!byAssistant[log.assistant_id]) byAssistant[log.assistant_id] = [];
    byAssistant[log.assistant_id].push(log);
  }

  return Object.entries(byAssistant).map(([assistantId, aLogs]) => {
    // Keep independent doctor/clinic streams separate. Otherwise a check-in
    // under one linked doctor could be paired with a check-out under another.
    const byAssignment: Record<string, AttendanceLog[]> = {};
    aLogs.forEach((log) => {
      const assignmentKey = `${log.doctor_id || ''}:${log.clinic_id || ''}`;
      (byAssignment[assignmentKey] ||= []).push(log);
    });
    const sessions = Object.values(byAssignment).flatMap((assignmentLogs) =>
      pairLogsToSessions(assignmentLogs, nowMs)
    );

    // Group sessions by date
    const byDate: Record<string, Session[]> = {};
    for (const s of sessions) {
      if (!byDate[s.date]) byDate[s.date] = [];
      byDate[s.date].push(s);
    }

    const days: DaySummary[] = Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, daySessions]) => ({
        key: `${assistantId}_${date}`,
        date,
        sessions: daySessions,
        totalMinutes: daySessions.reduce((sum, s) => sum + (s.durationMinutes ?? 0), 0),
      }));

    const totalMinutes = days.reduce((sum, d) => sum + d.totalMinutes, 0);
    const daysWorked = days.length;

    return {
      key: assistantId,
      assistantId,
      assistantName: aLogs[0].assistant_name || assistantId,
      totalSessions: sessions.length,
      daysWorked,
      totalMinutes,
      avgMinutesPerDay: daysWorked > 0 ? Math.round(totalMinutes / daysWorked) : 0,
      days,
    };
  }).sort((a, b) => b.totalMinutes - a.totalMinutes);
}

/* ─── Component ──────────────────────────────────────────────────────────────── */

const AttendanceManagement: React.FC = () => {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const { canManageOwnerFeatures } = useOwnerContext();
  const { doctorId } = useDoctorContext();
  const { clinics, loading: clinicsLoading, selectedClinicId } = useClinicContext();

  const [logs, setLogs] = useState<AttendanceLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('day'),
  ]);
  const [filterClinicId, setFilterClinicId] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState<string>('summary');
  const [payrollByAssistant, setPayrollByAssistant] = useState<Record<string, AssistantPayrollState>>({});
  const [, setPayrollLoading] = useState(false);
  const [savingPayrollAssistantId, setSavingPayrollAssistantId] = useState<string | null>(null);
  const [linkedAssistants, setLinkedAssistants] = useState<{ id: string; name: string }[]>([]);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [correctionTarget, setCorrectionTarget] = useState<Session | null>(null);
  const [correctionLoading, setCorrectionLoading] = useState(false);

  const payrollYearMonth = useMemo(() => {
    if (!dateRange?.[0]) return null;
    return dateRange[0].format('YYYY-MM');
  }, [dateRange]);

  const clinicIdForStaff = filterClinicId ?? selectedClinicId ?? clinics[0]?._id ?? null;

  useEffect(() => {
    const timer = window.setInterval(() => setClockNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const assistantSummariesFromLogs = useMemo(
    () => buildAssistantSummaries(logs, clockNow),
    [clockNow, logs]
  );

  const assistantSummaries = useMemo(() => {
    const fromLogs = assistantSummariesFromLogs;
    const seen = new Set(fromLogs.map((a) => a.assistantId));
    const extras: AssistantSummary[] = linkedAssistants
      .filter((a) => a.id && !seen.has(a.id))
      .map((a) => ({
        key: a.id,
        assistantId: a.id,
        assistantName: a.name || a.id,
        totalSessions: 0,
        daysWorked: 0,
        totalMinutes: 0,
        avgMinutesPerDay: 0,
        days: [],
      }));
    return [...fromLogs, ...extras].sort((a, b) => b.totalMinutes - a.totalMinutes);
  }, [assistantSummariesFromLogs, linkedAssistants]);

  const globalStats = useMemo(() => {
    const totalMinutes = assistantSummaries.reduce((s, a) => s + a.totalMinutes, 0);
    const totalSessions = assistantSummaries.reduce((s, a) => s + a.totalSessions, 0);
    const totalDays = assistantSummaries.reduce((s, a) => s + a.daysWorked, 0);
    return {
      assistants: assistantSummaries.length,
      totalSessions,
      totalDays,
      totalMinutes,
    };
  }, [assistantSummaries]);

  const fetchLogs = useCallback(async () => {
    if (!doctorId || !dateRange?.[0] || !dateRange?.[1]) return;
    setLogsLoading(true);
    try {
      const baseParams: Record<string, string> = {
        from: dateRange[0].startOf('day').toISOString(),
        to: dateRange[1].endOf('day').toISOString(),
        limit: '2000',
      };
      const clinicIds = filterClinicId
        ? [filterClinicId]
        : clinics.map((clinic) => clinic._id).filter(Boolean);
      if (clinicIds.length === 0 && selectedClinicId && selectedClinicId !== '__all_clinics__') {
        clinicIds.push(selectedClinicId);
      }

      // Assistant events are stored under the doctor selected in the assistant
      // portal. Owners can be signed in under another doctor ID, so query every
      // linked doctor/clinic pair visible to the owner instead of only doctorId.
      const scopePairs = clinicIds.length > 0
        ? (await Promise.all(clinicIds.map(async (clinicId) => {
            const doctors = await getClinicDoctorOptions(clinicId, doctorId);
            return doctors.map((doctor) => ({ clinicId, doctorId: doctor.id }));
          }))).flat()
        : [{ clinicId: '', doctorId }];
      const uniquePairs = Array.from(new Map(
        scopePairs.map((pair) => [`${pair.clinicId}:${pair.doctorId}`, pair])
      ).values());

      const responses = await Promise.all(uniquePairs.map(async (pair) => {
        const params = { ...baseParams };
        if (pair.clinicId) params.clinic_id = pair.clinicId;
        try {
          return await axios.get(
            `${API.BASE_URL}${API.ENDPOINTS.ATTENDANCE_LOGS(pair.doctorId)}`,
            { params }
          );
        } catch (error) {
          console.warn('[assistant-attendance] Failed to load one doctor/clinic scope', pair, error);
          return null;
        }
      }));
      if (responses.length > 0 && responses.every((response) => response === null)) {
        throw new Error('All assistant attendance requests failed');
      }
      const logsById = new Map<string, AttendanceLog>();
      responses.forEach((response) => {
        (response?.data?.logs || []).forEach((log: AttendanceLog) => logsById.set(log._id, log));
      });
      setLogs(Array.from(logsById.values()));
    } catch {
      message.error(t('error'));
    } finally {
      setLogsLoading(false);
    }
  }, [clinics, doctorId, dateRange, filterClinicId, selectedClinicId, t]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    if (!doctorId || clinicsLoading) return;
    if (!clinicIdForStaff) {
      setLinkedAssistants([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const doctors = await getClinicDoctorOptions(clinicIdForStaff, doctorId);
        const lists = await Promise.all(doctors.map((doctor) =>
          getAssistantsForDoctorClinic(doctor.id, clinicIdForStaff)
        ));
        if (cancelled) return;
        const assistantsById = new Map<string, { id: string; name?: string; email?: string }>();
        lists.flat().forEach((assistant: { id: string; name?: string; email?: string }) => {
          if (assistant.id) assistantsById.set(assistant.id, assistant);
        });
        setLinkedAssistants(
          Array.from(assistantsById.values()).map((a) => ({
            id: a.id,
            name: a.name || a.email || a.id,
          }))
        );
      } catch {
        if (!cancelled) setLinkedAssistants([]);
      }
    })();
    return () => { cancelled = true; };
  }, [doctorId, clinicIdForStaff, clinicsLoading]);

  const fetchPayroll = useCallback(async () => {
    if (!doctorId || !payrollYearMonth) return;
    setPayrollLoading(true);
    try {
      const res = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.ATTENDANCE_PAYROLL(doctorId)}`, {
        params: { year_month: payrollYearMonth },
      });
      if (!res.data?.success) return;
      const map: Record<string, AssistantPayrollState> = {};
      for (const r of res.data.records || []) {
        const aid = r.assistant_id;
        const wdRaw = Number(r.working_days_in_month);
        const wd = wdRaw > 0 ? wdRaw : (payrollYearMonth ? countWeekdaysMonFri(payrollYearMonth) : 22);
        map[aid] = {
          hourlyRate: Number(r.hourly_rate) || 0,
          monthlySalary: Number(r.monthly_contract_salary) || 0,
          hoursPerDay: r.hours_per_day != null ? Number(r.hours_per_day) : 8,
          workingDays: wd,
          lines: (r.adjustment_lines || []).map((l: { _id?: string; amount?: number; reason?: string; created_at?: string }, i: number) => {
            const amount = Number(l.amount) || 0;
            return {
              clientKey: String(l._id || `imp_${i}_${l.created_at || ''}`),
              amount,
              reason: l.reason || '',
              created_at: l.created_at,
              lineKind: amount < 0 ? 'discount' : 'bonus',
            };
          }),
        };
      }
      setPayrollByAssistant(map);
    } catch {
      message.error(t('error'));
    } finally {
      setPayrollLoading(false);
    }
  }, [doctorId, payrollYearMonth, t]);

  useEffect(() => { fetchPayroll(); }, [fetchPayroll]);

  const saveAssistantPayroll = useCallback(async (assistantId: string) => {
    if (!doctorId || !payrollYearMonth) return;
    const state = payrollByAssistant[assistantId] ?? defaultAssistantPayroll(payrollYearMonth);
    const expH = expectedContractHours(state);
    if (state.monthlySalary > 0 && expH <= 0) {
      message.warning(t('attendanceContractIncomplete'));
      return;
    }
    if (state.monthlySalary <= 0 && effectiveHourlyRate(state) <= 0) {
      message.warning(t('attendanceNeedRateOrContract'));
      return;
    }
    const eff = effectiveHourlyRate(state);
    setSavingPayrollAssistantId(assistantId);
    try {
      await axios.put(`${API.BASE_URL}${API.ENDPOINTS.ATTENDANCE_PAYROLL_UPSERT}`, {
        doctor_id: doctorId,
        assistant_id: assistantId,
        year_month: payrollYearMonth,
        hourly_rate: eff,
        monthly_contract_salary: state.monthlySalary,
        hours_per_day: state.hoursPerDay,
        working_days_in_month: state.workingDays,
        adjustment_lines: state.lines.map((line) => ({
          amount: line.amount,
          reason: line.reason,
          ...(line.created_at ? { created_at: line.created_at } : {}),
        })),
      });
      message.success(t('attendancePayrollSaved'));
      fetchPayroll();
    } catch {
      message.error(t('error'));
    } finally {
      setSavingPayrollAssistantId(null);
    }
  }, [doctorId, payrollYearMonth, payrollByAssistant, t, fetchPayroll]);

  const clinicDisplayName = (c: { name?: string; _id: string }) => c.name || c._id;

  const submitCorrection = async (correctedSignOutAt: string, note: string) => {
    if (!correctionTarget || !doctorId) return;
    setCorrectionLoading(true);
    try {
      await axios.patch(
        `${API.BASE_URL}${API.ENDPOINTS.ATTENDANCE_CORRECT_MISSING_CHECKOUT(doctorId, correctionTarget.clinicId, correctionTarget.signInLogId)}`,
        { corrected_sign_out_at: correctedSignOutAt, correction_note: note }
      );
      message.success(t('attendanceCorrectionSaved'));
      setCorrectionTarget(null);
      await fetchLogs();
    } catch (error: any) {
      message.error(error?.response?.data?.message || t('error'));
    } finally {
      setCorrectionLoading(false);
    }
  };

  /* ── Raw log columns ── */
  const logColumns = [
    {
      title: t('time'), dataIndex: 'createdAt', key: 't', width: 175,
      render: (d: string) => dayjs(d).format('YYYY-MM-DD HH:mm:ss'),
    },
    { title: t('attendanceAssistant'), dataIndex: 'assistant_name', key: 'an' },
    {
      title: t('attendanceAction'), dataIndex: 'action', key: 'a', width: 110,
      render: (a: string) => (
        <Tag color={a === 'sign_in' ? 'green' : 'red'}>
          {a === 'sign_in' ? t('attendanceSignIn') : t('attendanceSignOut')}
        </Tag>
      ),
    },
    {
      title: t('attendanceClinic'), dataIndex: 'clinic_name', key: 'cn',
      render: (v: string, r: AttendanceLog) => v || r.clinic_id,
    },
    {
      title: t('attendanceDistanceM'), dataIndex: 'distance_from_center_m', key: 'dm', width: 110,
      render: (n: number) => (n != null ? `${n} m` : '—'),
    },
  ];

  /* ── Session inner table columns (inside expandable day row) ── */
  const sessionColumns = [
    {
      title: t('attendanceClinic'), dataIndex: 'clinicName', key: 'clinic', width: 150,
    },
    {
      title: t('attendanceSignInTime'), dataIndex: 'signInAt', key: 'in',
      render: (d: string | null) => d
        ? dayjs(d).format('HH:mm:ss')
        : <Tag color="orange">{t('attendanceMissingCheckin')}</Tag>,
    },
    {
      title: t('attendanceSignOutTime'), dataIndex: 'signOutAt', key: 'out',
      render: (d: string | null, row: Session) =>
        d ? dayjs(d).format('HH:mm:ss') : (
          <Space wrap>
            <Tag color={row.needsReview ? 'orange' : 'blue'}>
              {row.reviewReason === 'missing_sign_in'
                ? t('attendanceMissingCheckin')
                : row.needsReview ? t('attendanceMissingCheckout') : t('attendanceStillCheckedIn')}
            </Tag>
            {canManageOwnerFeatures && (
              <Button size="small" onClick={() => setCorrectionTarget(row)}>
                {t('attendanceCorrectMissingCheckout')}
              </Button>
            )}
          </Space>
        ),
    },
    {
      title: t('attendanceDuration'), dataIndex: 'durationMinutes', key: 'dur',
      render: (m: number | null) => (
        <Text strong style={{ color: m !== null ? '#1677ff' : '#aaa' }}>
          {formatDuration(m)}
        </Text>
      ),
    },
  ];

  /* ── Day inner table columns (inside expandable assistant row) ── */
  const dayColumns = [
    {
      title: t('attendanceDate'), dataIndex: 'date', key: 'date', width: 130,
      render: (d: string) => dayjs(d).format('ddd, DD MMM YYYY'),
    },
    {
      title: t('attendanceSessions'), dataIndex: 'sessions', key: 'sc',
      render: (s: Session[]) => s.filter((x) => x.durationMinutes !== null).length,
    },
    {
      title: t('attendanceTotalHours'), dataIndex: 'totalMinutes', key: 'tm',
      render: (m: number) => (
        <Text strong style={{ color: '#1677ff' }}>{formatDuration(m)}</Text>
      ),
    },
  ];

  /* ── Assistant summary columns ── */
  const summaryColumns = [
    {
      title: t('attendanceAssistant'), dataIndex: 'assistantName', key: 'name',
      render: (n: string) => <Text strong><UserOutlined style={{ marginRight: 6 }} />{n}</Text>,
    },
    {
      title: t('attendanceSessions'), dataIndex: 'totalSessions', key: 'sessions',
      render: (n: number) => <Tag color="blue">{n}</Tag>,
    },
    {
      title: t('attendanceDaysWorked'), dataIndex: 'daysWorked', key: 'days',
      render: (n: number) => <Tag color="purple">{n}</Tag>,
    },
    {
      title: t('attendanceTotalHours'), dataIndex: 'totalMinutes', key: 'total',
      sorter: (a: AssistantSummary, b: AssistantSummary) => a.totalMinutes - b.totalMinutes,
      defaultSortOrder: 'descend' as const,
      render: (m: number) => (
        <Text strong style={{ fontSize: 15, color: '#1677ff' }}>
          {formatDuration(m)}
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
            ({formatHoursDecimal(m)})
          </Text>
        </Text>
      ),
    },
    {
      title: t('attendanceAvgPerDay'), dataIndex: 'avgMinutesPerDay', key: 'avg',
      render: (m: number) => <Text>{formatDuration(m)}</Text>,
    },
  ];

  const expandedDayRender = (day: DaySummary) => (
    <Table
      rowKey="key"
      dataSource={day.sessions}
      columns={sessionColumns}
      pagination={false}
      size="small"
      style={{ margin: '4px 0' }}
    />
  );

  const expandedAssistantRender = (assistant: AssistantSummary) => {
    const state = payrollByAssistant[assistant.assistantId] ?? defaultAssistantPayroll(payrollYearMonth);
    const minutesMonth = minutesInYearMonth(assistant, payrollYearMonth);
    const hoursDec = minutesMonth / 60;
    const expContractH = expectedContractHours(state);
    const contractActive = state.monthlySalary > 0 && expContractH > 0;
    const effectiveHr = effectiveHourlyRate(state);
    const basePay = Math.round(hoursDec * effectiveHr * 100) / 100;
    const adjTotal = state.lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
    const netPay = Math.round((basePay + adjTotal) * 100) / 100;
    const monthTitle = payrollYearMonth ? dayjs(`${payrollYearMonth}-01`).format('MMMM YYYY') : '';

    const mergeState = (patch: Partial<AssistantPayrollState>) => {
      setPayrollByAssistant((prev) => {
        const cur = prev[assistant.assistantId] ?? defaultAssistantPayroll(payrollYearMonth);
        return { ...prev, [assistant.assistantId]: { ...cur, ...patch } };
      });
    };

    const patchHourly = (hourlyRate: number) => mergeState({ hourlyRate });

    const updateLine = (clientKey: string, patch: Partial<AssistantPayrollLine>) => {
      setPayrollByAssistant((prev) => {
        const cur = prev[assistant.assistantId] ?? defaultAssistantPayroll(payrollYearMonth);
        const lines = cur.lines.map((ln) => (ln.clientKey === clientKey ? { ...ln, ...patch } : ln));
        return { ...prev, [assistant.assistantId]: { ...cur, lines } };
      });
    };

    const removeLine = (clientKey: string) => {
      setPayrollByAssistant((prev) => {
        const cur = prev[assistant.assistantId] ?? defaultAssistantPayroll(payrollYearMonth);
        return {
          ...prev,
          [assistant.assistantId]: { ...cur, lines: cur.lines.filter((ln) => ln.clientKey !== clientKey) },
        };
      });
    };

    const addBonusLine = () => {
      setPayrollByAssistant((prev) => {
        const cur = prev[assistant.assistantId] ?? defaultAssistantPayroll(payrollYearMonth);
        const nk = `n_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        return {
          ...prev,
          [assistant.assistantId]: {
            ...cur,
            lines: [...cur.lines, { clientKey: nk, amount: 0, reason: '', lineKind: 'bonus' }],
          },
        };
      });
    };

    const addDiscountLine = () => {
      setPayrollByAssistant((prev) => {
        const cur = prev[assistant.assistantId] ?? defaultAssistantPayroll(payrollYearMonth);
        const nk = `n_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        return {
          ...prev,
          [assistant.assistantId]: {
            ...cur,
            lines: [...cur.lines, { clientKey: nk, amount: 0, reason: '', lineKind: 'discount' }],
          },
        };
      });
    };

    const applyWeekdayCount = () => {
      if (!payrollYearMonth) return;
      mergeState({ workingDays: countWeekdaysMonFri(payrollYearMonth) });
    };

    return (
      <>
        {/* ── Payroll Panel ── */}
        <div style={{ padding: '4px 0 12px', maxWidth: 920 }}>

          {/* Header bar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 16, flexWrap: 'wrap', gap: 10,
          }}>
            <div>
              <Text strong style={{ fontSize: 16 }}>
                <DollarOutlined style={{ color: '#1677ff', marginRight: 8 }} />
                {t('attendanceSalarySection')}
              </Text>
              {payrollYearMonth ? (
                <Tag color="blue" style={{ marginLeft: 10, fontSize: 13 }}>{monthTitle}</Tag>
              ) : null}
            </div>
            <Button
              type="primary"
              size="large"
              loading={savingPayrollAssistantId === assistant.assistantId}
              onClick={() => saveAssistantPayroll(assistant.assistantId)}
              style={{ borderRadius: 8, paddingLeft: 28, paddingRight: 28 }}
            >
              {t('attendanceSavePayroll')}
            </Button>
          </div>

          {/* ── STEP 1: Salary setup ── */}
          <div style={{
            background: '#fff', border: '1.5px solid #e6f4ff', borderRadius: 12,
            padding: '16px 20px', marginBottom: 14,
          }}>
            <Text type="secondary" style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', display: 'block', marginBottom: 12 }}>
              {t('payrollStep1Label')}
            </Text>
            <Row gutter={[16, 16]} align="bottom">
              <Col xs={24} sm={8}>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>
                  {t('attendanceMonthlySalary')}
                  <Text type="secondary" style={{ fontWeight: 400, marginLeft: 6, fontSize: 12 }}>
                    ({t('attendanceCurrencyHint')})
                  </Text>
                </Text>
                <InputNumber
                  size="large"
                  min={0}
                  step={100}
                  style={{ width: '100%' }}
                  placeholder="e.g. 5000"
                  value={state.monthlySalary || undefined}
                  onChange={(v) => mergeState({ monthlySalary: v === null || v === undefined ? 0 : Number(v) })}
                />
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                  {t('payrollLeaveZeroForHourly')}
                </Text>
              </Col>
              <Col xs={12} sm={5}>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>
                  {t('attendanceHoursPerDay')}
                </Text>
                <InputNumber
                  size="large"
                  min={0.5}
                  max={24}
                  step={0.5}
                  style={{ width: '100%' }}
                  value={state.hoursPerDay || undefined}
                  onChange={(v) => mergeState({ hoursPerDay: v === null || v === undefined ? 8 : Number(v) })}
                />
              </Col>
              <Col xs={12} sm={5}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Text strong>{t('attendanceWorkingDays')}</Text>
                  <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 11 }} onClick={applyWeekdayCount}>
                    {t('attendanceUseWeekdayCount')}
                  </Button>
                </div>
                <InputNumber
                  size="large"
                  min={0}
                  max={31}
                  step={1}
                  style={{ width: '100%' }}
                  value={state.workingDays || undefined}
                  onChange={(v) => mergeState({ workingDays: v === null || v === undefined ? 0 : Number(v) })}
                />
              </Col>
              <Col xs={24} sm={6}>
                {contractActive ? (
                  <div style={{
                    background: '#f6ffed', border: '1.5px solid #b7eb8f', borderRadius: 10,
                    padding: '10px 14px', textAlign: 'center',
                  }}>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                      {t('attendanceImpliedHourlyRate')}
                    </Text>
                    <Text strong style={{ fontSize: 22, color: '#389e0d' }}>{effectiveHr.toFixed(2)}</Text>
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>{t('attendanceCurrencyHint')}/h</Text>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                      {expContractH}h expected
                    </Text>
                  </div>
                ) : (
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 6 }}>
                      {t('attendanceHourlyRateManual')}
                      <Text type="secondary" style={{ fontWeight: 400, marginLeft: 6, fontSize: 12 }}>
                        ({t('attendanceCurrencyHint')}/h)
                      </Text>
                    </Text>
                    <InputNumber
                      size="large"
                      min={0}
                      step={0.5}
                      style={{ width: '100%' }}
                      placeholder="e.g. 25"
                      value={state.hourlyRate > 0 ? state.hourlyRate : undefined}
                      onChange={(v) => patchHourly(v === null || v === undefined ? 0 : Number(v))}
                    />
                    {state.monthlySalary > 0 && expContractH <= 0 ? (
                      <Text type="danger" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                        {t('attendanceContractHoursWarning')}
                      </Text>
                    ) : null}
                  </div>
                )}
              </Col>
            </Row>
          </div>

          {/* ── STEP 2: Pay summary cards ── */}
          <Row gutter={[12, 12]} style={{ marginBottom: 14 }}>
            <Col xs={8}>
              <div style={{
                background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 12,
                padding: '12px 16px', textAlign: 'center',
              }}>
                <Text type="secondary" style={{ fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase', display: 'block' }}>
                  {t('attendanceHoursInPayrollMonth')}
                </Text>
                <Text strong style={{ fontSize: 22, color: '#2f54eb', display: 'block', lineHeight: 1.4 }}>
                  {formatHoursDecimal(minutesMonth)}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>{formatDuration(minutesMonth)}</Text>
              </div>
            </Col>
            <Col xs={8}>
              <div style={{
                background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 12,
                padding: '12px 16px', textAlign: 'center',
              }}>
                <Text type="secondary" style={{ fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase', display: 'block' }}>
                  {t('attendanceBaseSalary')}
                </Text>
                <Text strong style={{ fontSize: 22, display: 'block', lineHeight: 1.4 }}>
                  {basePay.toFixed(2)}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>{t('attendanceCurrencyHint')}</Text>
              </div>
            </Col>
            <Col xs={8}>
              <div style={{
                background: adjTotal >= 0 ? '#f6ffed' : '#fff1f0',
                border: `1.5px solid ${adjTotal >= 0 ? '#73d13d' : '#ff7875'}`,
                borderRadius: 12, padding: '12px 16px', textAlign: 'center',
              }}>
                <Text type="secondary" style={{ fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase', display: 'block' }}>
                  {t('attendanceNetSalary')}
                </Text>
                <Text strong style={{ fontSize: 24, color: '#1677ff', display: 'block', lineHeight: 1.4 }}>
                  {netPay.toFixed(2)}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>{t('attendanceCurrencyHint')}</Text>
              </div>
            </Col>
          </Row>

          {/* ── STEP 3: Bonuses & Discounts ── */}
          <div style={{
            background: '#fff', border: '1px solid #f0f0f0', borderRadius: 12,
            padding: '14px 16px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <Text strong style={{ fontSize: 14 }}>{t('attendanceAdjustmentsTitle')}</Text>
              <Space size="small">
                <Button
                  icon={<PlusOutlined />}
                  style={{ borderColor: '#52c41a', color: '#52c41a', borderRadius: 8 }}
                  onClick={addBonusLine}
                >
                  {t('attendanceAddBonus')}
                </Button>
                <Button
                  icon={<MinusOutlined />}
                  danger
                  style={{ borderRadius: 8 }}
                  onClick={addDiscountLine}
                >
                  {t('attendanceAddDiscount')}
                </Button>
              </Space>
            </div>

            {state.lines.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '24px 0', color: '#bfbfbf',
                border: '1.5px dashed #e8e8e8', borderRadius: 10,
              }}>
                <DollarOutlined style={{ fontSize: 26, marginBottom: 8, display: 'block' }} />
                <Text type="secondary">{t('attendanceNoAdjustmentLines')}</Text>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {state.lines.map((line) => {
                  const kind = effectiveLineKind(line);
                  const isBonus = kind === 'bonus';
                  return (
                    <div
                      key={line.clientKey}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        background: isBonus ? '#f6ffed' : '#fff1f0',
                        border: `1.5px solid ${isBonus ? '#b7eb8f' : '#ffa39e'}`,
                        borderRadius: 10, padding: '10px 14px',
                      }}
                    >
                      <Tag
                        color={isBonus ? 'success' : 'error'}
                        style={{ minWidth: 78, textAlign: 'center', fontSize: 13, margin: 0 }}
                      >
                        {isBonus ? `＋ ${t('attendanceLineTypeBonus')}` : `－ ${t('attendanceLineTypeDiscount')}`}
                      </Tag>
                      <InputNumber
                        min={0}
                        step={0.5}
                        style={{ width: 130 }}
                        placeholder="Amount"
                        addonAfter={t('attendanceCurrencyHint')}
                        value={isBonus
                          ? (line.amount > 0 ? line.amount : undefined)
                          : (line.amount === 0 ? undefined : Math.abs(Number(line.amount) || 0))}
                        onChange={(v) => updateLine(line.clientKey, {
                          amount: v === null || v === undefined ? 0
                            : isBonus ? Math.abs(Number(v)) : -Math.abs(Number(v)),
                          lineKind: kind,
                        })}
                      />
                      <Input
                        style={{ flex: 1, minWidth: 160 }}
                        placeholder={isBonus ? t('attendanceBonusReasonPh') : t('attendanceDiscountReasonPh')}
                        value={line.reason}
                        onChange={(e) => updateLine(line.clientKey, { reason: e.target.value })}
                      />
                      <Tooltip title={t('delete')}>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeLine(line.clientKey)}
                          style={{ flexShrink: 0 }}
                        />
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
            )}

            {adjTotal !== 0 && (
              <div style={{ textAlign: 'right', marginTop: 10 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('payrollTotalAdjustments')}:{' '}
                  <Text strong style={{ color: adjTotal > 0 ? '#52c41a' : '#ff4d4f' }}>
                    {adjTotal > 0 ? '+' : ''}{adjTotal.toFixed(2)} {t('attendanceCurrencyHint')}
                  </Text>
                </Text>
              </div>
            )}
          </div>
        </div>
        <Table
          rowKey="key"
          dataSource={assistant.days}
          columns={dayColumns}
          pagination={false}
          size="small"
          expandable={{ expandedRowRender: expandedDayRender }}
          style={{ margin: '4px 0' }}
          summary={(data) => {
            const total = data.reduce((s, d) => s + d.totalMinutes, 0);
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><Text strong>{t('total')}</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={1} />
                <Table.Summary.Cell index={2}>
                  <Text strong style={{ color: '#1677ff' }}>{formatDuration(total)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </>
    );
  };

  if (!doctorId) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Content style={{ padding: 24 }}><Spin /></Content>
      </Layout>
    );
  }

  const periodLabel = dateRange
    ? `${dateRange[0].format('DD MMM')} – ${dateRange[1].format('DD MMM YYYY')}`
    : '';

  return (
    <Layout style={{ minHeight: '100vh', direction: isRTL ? 'rtl' : 'ltr' }}>
      <Header style={{ background: '#fff', padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')}>{t('backToDashboard')}</Button>
        <Title level={4} style={{ margin: 0 }}>
          <ClockCircleOutlined /> {t('attendanceTitle')}
        </Title>
      </Header>

      <Content style={{ padding: 24 }}>

        <ClinicAttendanceZoneSettings />

        {/* ── Filters ── */}
        <Card style={{ marginBottom: 16 }}>
          <Space wrap>
            <RangePicker
              value={dateRange}
              onChange={(v) => v && v[0] && v[1] && setDateRange([v[0], v[1]])}
            />
            <Select
              allowClear
              placeholder={t('attendanceFilterClinic')}
              style={{ minWidth: 200 }}
              value={filterClinicId}
              onChange={setFilterClinicId}
              options={clinics.map((c) => ({ value: c._id, label: clinicDisplayName(c) }))}
            />
            <Button onClick={fetchLogs} loading={logsLoading}>{t('attendanceRefreshLogs')}</Button>
          </Space>
        </Card>

        {/* ── Summary stat cards ── */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title={t('attendanceTotalAssistants')}
                value={globalStats.assistants}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#1677ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title={t('attendanceSessions')}
                value={globalStats.totalSessions}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title={t('attendanceDaysWorked')}
                value={globalStats.totalDays}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Tooltip title={periodLabel}>
                <Statistic
                  title={t('attendanceTotalHours')}
                  value={formatHoursDecimal(globalStats.totalMinutes)}
                  prefix={<FieldTimeOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                  suffix={`(${formatDuration(globalStats.totalMinutes)})`}
                />
              </Tooltip>
            </Card>
          </Col>
        </Row>

        {/* ── Tabs: Summary | Raw Logs ── */}
        <Card>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'summary',
                label: <span><UserOutlined />{t('attendanceSummaryTab')}</span>,
                children: (
                  <>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={t('attendancePayrollHint')}
                    />
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                      {t('attendanceSummaryHelp')}
                    </Text>
                    {logsLoading ? (
                      <Spin />
                    ) : assistantSummaries.length === 0 ? (
                      <Space direction="vertical" size="small">
                        <Text type="secondary">{t('attendanceNoSessions')}</Text>
                        <Text type="secondary">{t('attendancePayrollNoRowsHint')}</Text>
                      </Space>
                    ) : (
                      <Table
                        rowKey="key"
                        dataSource={assistantSummaries}
                        columns={summaryColumns}
                        pagination={false}
                        expandable={{
                          expandedRowRender: expandedAssistantRender,
                        }}
                        scroll={{ x: true }}
                        summary={(data) => {
                          const totMin = data.reduce((s, a) => s + a.totalMinutes, 0);
                          const totSess = data.reduce((s, a) => s + a.totalSessions, 0);
                          const totDays = data.reduce((s, a) => s + a.daysWorked, 0);
                          return (
                            <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 700 }}>
                              <Table.Summary.Cell index={0}>
                                <Text strong>{t('total')}</Text>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={1}>
                                <Tag color="blue">{totSess}</Tag>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={2}>
                                <Tag color="purple">{totDays}</Tag>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={3}>
                                <Text strong style={{ fontSize: 15, color: '#fa8c16' }}>
                                  {formatDuration(totMin)}{' '}
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    ({formatHoursDecimal(totMin)})
                                  </Text>
                                </Text>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={4} />
                            </Table.Summary.Row>
                          );
                        }}
                      />
                    )}
                  </>
                ),
              },
              {
                key: 'raw',
                label: <span><ClockCircleOutlined />{t('attendanceRawLogsTab')}</span>,
                children: (
                  <Table
                    rowKey="_id"
                    dataSource={logs}
                    columns={logColumns}
                    loading={logsLoading}
                    scroll={{ x: true }}
                    pagination={{ pageSize: 20 }}
                  />
                ),
              },
            ]}
          />
        </Card>
      </Content>
      <MissingCheckoutCorrectionModal
        open={Boolean(correctionTarget)}
        signInAt={correctionTarget?.signInAt || null}
        loading={correctionLoading}
        onCancel={() => setCorrectionTarget(null)}
        onSubmit={submitCorrection}
      />
    </Layout>
  );
};

export default AttendanceManagement;
